import { useQuery } from '@tanstack/react-query';
import type { paths } from '@bastion/api';
import { authenticatedFetch } from '../authenticatedFetch';
import type { CloudEpisode, EpisodeQueryFilters } from '../types';
import { fetchListPages } from './listPages';
import { resolveReviewedAgainstFilters } from './reviewedAgainstFilters';
import { values } from '../utils/filterValues';
import {
  CLOUD_EPISODE_COUNT_QUERY_KEY,
  CLOUD_EPISODES_QUERY_KEY,
} from './episodeCollectionQueries';

/**
 * The wire types come from the server's OpenAPI schema, so a route change
 * fails the type check here. Regenerate them with
 * `npm run --workspace=@bastion/api generate-types`.
 */
type EpisodeListResponse =
  paths['/episodes']['get']['responses']['200']['content']['application/json'];
export type EpisodeListQuery = NonNullable<paths['/episodes']['get']['parameters']['query']>;
type EpisodeDetailResponse =
  paths['/episodes/{episode_id}']['get']['responses']['200']['content']['application/json'];

export interface CloudEpisodesResult {
  data: CloudEpisode[];
}

export function uniqueNonEmptyIds(
  ids: EpisodeQueryFilters['id'],
): string[] {
  return Array.from(new Set(values(ids).filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  )));
}

export function episodeCountFilters(filters: EpisodeQueryFilters): EpisodeQueryFilters {
  const {
    limit: _limit,
    offset: _offset,
    sort_by: _sortBy,
    sort_order: _sortOrder,
    ...countFilters
  } = filters;
  void _limit;
  void _offset;
  void _sortBy;
  void _sortOrder;
  return countFilters;
}

/** The filters that name a set of values. Each one repeats in the query string. */
const LIST_FILTER_KEYS = [
  'id', 'id_nin',
  'source', 'source_nin',
  'rating', 'rating_nin',
  'tag', 'tag_nin',
  'hostname', 'hostname_nin',
  'hardware_version', 'hardware_version_nin',
  'sop_id', 'sop_id_nin',
  'source_artifact_id', 'source_artifact_id_nin',
  'pinned_checkpoint_id', 'pinned_checkpoint_id_nin',
  'collector_id', 'collector_id_nin',
  'reviewed', 'has_interventions',
  'subtask_coverage', 'subtask_label',
] as const satisfies readonly (keyof EpisodeQueryFilters & keyof EpisodeListQuery)[];

/** The filters that carry one value. */
const SCALAR_FILTER_KEYS = [
  'task_search', 'date_from', 'date_to', 'duration_min', 'duration_max',
  'sort_by', 'sort_order', 'limit', 'offset',
] as const satisfies readonly (keyof EpisodeQueryFilters & keyof EpisodeListQuery)[];

/**
 * Every query parameter the route declares appears in one of the two lists
 * above. A filter the server adds that this hook never sends resolves to that
 * filter's name here instead of `never`, and the type check fails, so a new
 * server filter cannot go unsent without someone deciding to leave it out.
 */
type UnsentServerFilters = Exclude<
  keyof EpisodeListQuery,
  (typeof LIST_FILTER_KEYS)[number] | (typeof SCALAR_FILTER_KEYS)[number]
>;
export type AssertEveryServerFilterIsSent<T extends never = UnsentServerFilters> = T;

/**
 * Build the query string of an episode list request.
 *
 * A filter that names several values repeats its key, which is the form
 * FastAPI reads a list parameter in. The server rejects an unknown value with
 * 422, so a filter never reaches the database as text.
 *
 * `useDatasetEpisodes` builds the same parameters against its own path. Both
 * lists therefore send one filter one way, and a search for `50%` means the
 * same thing on each.
 */
export function buildEpisodeFilterParams(filters: EpisodeQueryFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of LIST_FILTER_KEYS) {
    for (const value of values(filters[key])) {
      if (value === undefined || value === null || value === '') continue;
      params.append(key, String(value));
    }
  }
  for (const key of SCALAR_FILTER_KEYS) {
    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  return params;
}

/** Build the episode list path. */
export function buildEpisodeListPath(filters: EpisodeQueryFilters): string {
  const search = buildEpisodeFilterParams(filters).toString();
  return search ? `/episodes?${search}` : '/episodes';
}

/**
 * The most rows one call reads, across its pages. Without the ceiling the
 * number of requests follows whatever a caller asks for. `fetchListPages`
 * explains why a read of more than one page is not one snapshot.
 */
const MAX_EPISODE_ROWS = 10000;

/**
 * List episodes.
 *
 * The server returns the episodes the caller's organization holds a grant on,
 * and applies the filters inside that same query. An episode the caller cannot
 * reach is missing from the page and from the count.
 *
 * The server defaults to the newest episodes first, and to a page of 100.
 */
export function useCloudEpisodes(
  filters: EpisodeQueryFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...CLOUD_EPISODES_QUERY_KEY, filters],
    enabled: options.enabled ?? true,
    queryFn: async ({ signal }): Promise<CloudEpisodesResult> => {
      // The "Reviewed against" chip resolves to an id filter first, so the
      // dataset list and the stats hook read it the same way this one does.
      const { filters: effectiveFilters, empty } =
        await resolveReviewedAgainstFilters(filters, signal);
      if (empty) return { data: [] };

      // A caller that names no usable limit reads the server's default page.
      // A limit of 0 or less would fail request validation with 422.
      const asked = effectiveFilters.limit;
      if (asked === undefined || asked <= 0) {
        const resp = await authenticatedFetch<EpisodeListResponse>(
          buildEpisodeListPath({ ...effectiveFilters, limit: undefined }),
          { signal },
        );
        return { data: resp.items as CloudEpisode[] };
      }

      const page = await fetchListPages<EpisodeListResponse['items'][number]>(
        (limit, offset) => buildEpisodeListPath({ ...effectiveFilters, limit, offset }),
        {
          limit: Math.min(asked, MAX_EPISODE_ROWS),
          offset: effectiveFilters.offset,
          signal,
        },
      );
      return { data: page.items as CloudEpisode[] };
    },
  });
}

/**
 * Count the episodes the filters match.
 *
 * The count comes from the list envelope, which counts inside the scoped
 * query, so it reports the episodes the caller can read and no other. The
 * request asks for one row because it reads `total` only.
 */
export function useCloudEpisodeCount(
  filters: EpisodeQueryFilters = {},
  options: { enabled?: boolean } = {},
) {
  const countFilters = episodeCountFilters(filters);

  return useQuery({
    queryKey: [...CLOUD_EPISODE_COUNT_QUERY_KEY, countFilters],
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    queryFn: async ({ signal }): Promise<number> => {
      const { filters: effectiveFilters, empty } =
        await resolveReviewedAgainstFilters(countFilters, signal);
      if (empty) return 0;

      const resp = await authenticatedFetch<EpisodeListResponse>(
        buildEpisodeListPath({ ...effectiveFilters, limit: 1, offset: 0 }),
        { signal },
      );
      return resp.total;
    },
  });
}

/**
 * Read one episode by id.
 *
 * The route returns every column, including `raw_metadata`, because the detail
 * view reads it. `prefetchReviewEpisode` seeds the cache entry
 * `useCloudEpisodeById` serves, so it reads through this function too and the
 * two payloads cannot drift apart.
 */
export async function readCloudEpisode(
  episodeId: string,
  signal?: AbortSignal,
): Promise<CloudEpisode> {
  const resp = await authenticatedFetch<EpisodeDetailResponse>(
    `/episodes/${encodeURIComponent(episodeId)}`,
    { signal },
  );
  return resp as CloudEpisode;
}

/** Fetch one episode by id. */
export function useCloudEpisodeById(episodeId: string | null) {
  return useQuery({
    queryKey: ['cloudEpisode', episodeId],
    queryFn: async ({ signal }): Promise<CloudEpisode | null> =>
      episodeId ? readCloudEpisode(episodeId, signal) : null,
    enabled: !!episodeId,
  });
}
